import { useCallback, useEffect, useState } from "react";
import { api, getToken, setToken, setUnauthorizedHandler, type Me } from "./lib/api.js";
import Login from "./pages/Login.js";
import Shell from "./pages/Shell.js";
import Session from "./pages/Session.js";
import MirrorWindow from "./pages/MirrorWindow.js";

/**
 * Routing is a hash route rather than history state: the console is served as
 * a static bundle from hbbx, and a hash never needs server-side rewrites.
 */
function useHashRoute(): [string, (to: string) => void] {
  const [route, setRoute] = useState(() => window.location.hash.slice(1) || "/machines");
  useEffect(() => {
    const onHash = () => setRoute(window.location.hash.slice(1) || "/machines");
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const navigate = useCallback((to: string) => {
    window.location.hash = to;
  }, []);
  return [route, navigate];
}

export default function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [checking, setChecking] = useState(true);
  const [route, navigate] = useHashRoute();

  const signOut = useCallback(() => {
    api.post("/api/logout", {}).catch(() => {
      /* the token is discarded either way */
    });
    setToken("");
    setMe(null);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => setMe(null));
  }, []);

  useEffect(() => {
    if (!getToken()) {
      setChecking(false);
      return;
    }
    api
      .get<Me>("/api/console/me")
      .then(setMe)
      .catch(() => setToken(""))
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="login">
        <span className="faint">Loading…</span>
      </div>
    );
  }

  if (!me) return <Login onSignedIn={setMe} />;

  // A live session takes over the whole viewport: the remote screen is the
  // subject and console chrome would only steal space from it.
  /*
   * A screen being shown in its own window, drawn by the window that holds
   * the connection. It has no session and no credential of its own - see
   * lib/mirror.ts for why.
   */
  if (route.startsWith("/mirror/")) {
    const [path, query] = route.slice("/mirror/".length).split("?");
    const params = new URLSearchParams(query ?? "");
    return (
      <MirrorWindow
        peerId={decodeURIComponent(path ?? "")}
        display={Number(params.get("display") ?? 0)}
      />
    );
  }

  if (route.startsWith("/session/")) {
    // A screen popped out into its own window carries which display it shows
    // and that it is a secondary window, so it opens without the toolbar
    // clutter of the main one.
    const [path, query] = route.slice("/session/".length).split("?");
    const params = new URLSearchParams(query ?? "");
    return (
      <Session
        peerId={decodeURIComponent(path ?? "")}
        me={me}
        initialDisplay={params.has("display") ? Number(params.get("display")) : undefined}
        secondaryWindow={params.get("window") === "1"}
        onExit={() =>
          params.get("window") === "1" ? window.close() : navigate("/machines")
        }
      />
    );
  }

  return <Shell me={me} route={route} navigate={navigate} onSignOut={signOut} />;
}
